import { useEffect, useState } from 'react'
import { api } from '@carrent/api'
import { DAMAGE_TYPE_LABELS, DamageSchema, truckProjections, zoneLabel } from '@carrent/ui'

/**
 * Сравнение актов приёмки и сдачи.
 *
 * То, ради чего фича задумывалась. Раньше спор «кто разбил» решался
 * так: диспетчер открывал шесть фото «до» и шесть «после» и глазами
 * искал разницу. Здесь расхождение считается за него — новые точки
 * акта сдачи и есть ответ.
 *
 * Обе схемы показываются рядом и в одной проекции: переключение
 * общее, иначе сравнивать приходится, помня, что было на другой
 * стороне машины.
 */

function MarkRow({ mark, tone, onOpen }) {
  return (
    <button type="button" className={`damage-diff__row damage-diff__row--${tone}`}
      onClick={() => onOpen(mark)}>
      <span className="damage-diff__row-zone">{zoneLabel(mark.zone_id)}</span>
      <span className="damage-diff__row-type">
        {DAMAGE_TYPE_LABELS[mark.damage_type] || mark.damage_type}
      </span>
      {mark.severity === 'critical' && (
        <span className="damage-diff__row-critical">критично</span>
      )}
    </button>
  )
}

export default function TripDamageCompare({ trip, delta = null }) {
  const [marks, setMarks] = useState(null)
  const [projection, setProjection] = useState('front')
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(null)

  const bodyType = trip.car?.body_type
  const projections = truckProjections(bodyType,
    { hasTailLift: trip.car?.has_tail_lift })

  useEffect(() => {
    let alive = true
    api.get(`/trips/${trip.id}/damage-marks`)
      .then(({ data }) => { if (alive) setMarks(data.data?.marks || { start: [], end: [] }) })
      .catch(() => { if (alive) setError('Не удалось загрузить отметки повреждений') })
    return () => { alive = false }
  }, [trip.id])

  if (!projections) {
    return (
      <p className="damage-diff__unsupported">
        Для этого типа кузова схема повреждений не ведётся.
      </p>
    )
  }

  const start = marks?.start || []
  const end = marks?.end || []

  // Расхождение считает сервер: то же правило раньше жило и здесь,
  // и в сервисе сравнения. Две копии одного правила расходятся молча —
  // экран показывал бы одно, а флаг поездки строился бы по другому.
  // Здесь остаётся только отрисовка.
  const appeared = delta?.added || []

  return (
    <div className="damage-diff">
      {error && <div className="alert alert--warning">{error}</div>}

      <div className="damage-diff__tabs" role="tablist" aria-label="Проекции кузова">
        {projections.map((p) => (
          <button key={p.key} type="button" role="tab"
            aria-selected={p.key === projection}
            className={`damage-diff__tab${p.key === projection ? ' is-active' : ''}`}
            onClick={() => setProjection(p.key)}>
            {p.label}
          </button>
        ))}
      </div>

      <div className="damage-diff__panes">
        <section className="damage-diff__pane">
          <h4 className="damage-diff__title">Повреждения при приёмке</h4>
          <DamageSchema projections={projections} activeKey={projection}
            marks={start.map((m) => ({ ...m, status: 'existing' }))}
            loading={!marks} disabled onMarkTap={setOpen} />
          <p className="damage-diff__count">Отмечено: {start.length}</p>
        </section>

        <section className="damage-diff__pane">
          <h4 className="damage-diff__title">Повреждения при сдаче</h4>
          <DamageSchema projections={projections} activeKey={projection}
            marks={end} loading={!marks} disabled onMarkTap={setOpen} />
          <p className="damage-diff__count">Отмечено: {end.length}</p>
        </section>
      </div>

      <div className={`damage-diff__verdict${appeared.length ? ' is-changed' : ''}`}>
        {marks && delta && (appeared.length === 0
          ? 'За поездку новых повреждений не зафиксировано.'
          : `Появилось за поездку: ${appeared.length}`)}
      </div>

      {appeared.length > 0 && (
        <div className="damage-diff__list">
          {appeared.map((m) => (
            <MarkRow key={m.id} mark={m} tone="new" onOpen={setOpen} />
          ))}
        </div>
      )}

      {open && (
        <div className="damage-diff__drawer" role="dialog" aria-label="Повреждение">
          <div className="damage-diff__drawer-head">
            <div>
              <p className="damage-diff__drawer-zone">{zoneLabel(open.zone_id)}</p>
              <p className="damage-diff__drawer-type">
                {DAMAGE_TYPE_LABELS[open.damage_type] || open.damage_type}
                {open.severity === 'critical' && ' · критично'}
              </p>
            </div>
            <button type="button" className="btn btn--ghost" onClick={() => setOpen(null)}>
              Закрыть
            </button>
          </div>

          {open.photo_url && (
            <img src={open.photo_url} alt="Фото повреждения" className="damage-diff__photo" />
          )}

          <dl className="damage-diff__meta">
            <div>
              <dt>Отмечено водителем</dt>
              <dd>{open.client_occurred_at || '—'}</dd>
            </div>
            <div>
              <dt>Получено сервером</dt>
              <dd>{open.created_at || '—'}</dd>
            </div>
          </dl>
          {open.client_occurred_at && open.created_at
            && open.client_occurred_at !== open.created_at && (
            <p className="damage-diff__meta-note">
              Отметка сделана офлайн и отправлена позже — это нормально
              для приёмки без связи.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
